// --------------------------------------------------------------
// Created On: 2021-09-01
// Author: Zachary Thomas
//
// Last Modified: 2023-11-17
// Modified By: Lilly Kane
//
// Copyright 2021 - 2022 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import {
  API,
  SECONDS_BETWEEN_DEVICE_READ_QUERIES,
  MAX_QUERIES_FOR_DEVICE_READ,
  MS_PER_SECOND,
} from "../../constants/miscellaneous";
import Spinner from "../Spinner/Spinner";
import { getCurrentUser } from "../../redux/selectors";
import { useSelector } from "react-redux";
import apiRequest from "../../utilities/api/apiRequest";
import PropTypes from "prop-types";
import styles from "./SampleDeviceDataButton.module.scss";

// When clicked, this button requests the current monitoring device to sample new data.
export default function SampleDeviceDataButton(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [queriesRemaining, setQueriesRemaining] = useState<number>(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number>(0);
  const [currentIsoTime, setCurrentIsoTime] = useState<string | null>(null);
  const currentUser = useSelector(getCurrentUser);

  // Poll sample data until we get new data or we time out.
  useEffect(() => {
    if (queriesRemaining > 0 && currentIsoTime !== null) {
      const timerId = setTimeout(
        () => checkDeviceUpdated(selectedDeviceId, currentIsoTime, queriesRemaining),
        MS_PER_SECOND * SECONDS_BETWEEN_DEVICE_READ_QUERIES
      );

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [queriesRemaining, selectedDeviceId, currentIsoTime]);

  // Sample device to get the most current device data.
  function sampleDevice(deviceId: number): void {
    apiRequest(`${API}/company/${currentUser.companyId}/device/${deviceId}/query`, "GET", null);

    const currentDate = new Date();
    if (props.onDeviceError !== undefined) {
      props.onDeviceError("");
    }
    setCurrentIsoTime(currentDate.toISOString().split(".")[0]);
    setSelectedDeviceId(deviceId);
    setQueriesRemaining(MAX_QUERIES_FOR_DEVICE_READ);
    setLoading(true);
  }

  // Check if a device's data has been updated and send the appropriate response.
  async function checkDeviceUpdated(
    deviceId: number,
    isoTime: string,
    queriesRemaining: number
  ): Promise<void> {
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/device/${deviceId}/hasupdated/${isoTime}`,
      "GET",
      null
    )) as [Response, ResponseBody];

    if (response.ok) {
      if (responseBody.hasUpdated) {
        // We see that the device has been updated successfully.
        setLoading(false);
        setCurrentIsoTime(null);
        setSelectedDeviceId(0);
        setQueriesRemaining(0);
        if (props.onDeviceRead !== undefined) {
          props.onDeviceRead();
        }
      } else if (queriesRemaining <= 1) {
        // The device failed to update.
        setLoading(false);
        setCurrentIsoTime(null);
        setSelectedDeviceId(0);
        setQueriesRemaining(0);
        if (props.onDeviceError !== undefined) {
          props.onDeviceError("Monitoring device was unable to take sample.");
        }
      } else {
        // The device is still in the process of updating.
        setQueriesRemaining(queriesRemaining - 1);
      }
    } else {
      // If we get an error, stop trying to process a device update.
      setLoading(false);
      setCurrentIsoTime(null);
      setSelectedDeviceId(0);
      setQueriesRemaining(0);
      if (props.onDeviceError !== undefined) {
        props.onDeviceError("Monitoring device was unable to take sample.");
      }
    }
  }

  return (
    <Fragment>
      <Spinner loading={loading} />
      <button
        data-test="sample-device-button"
        className={`${styles.body} btn btn-primary`}
        type="button"
        disabled={selectedDeviceId > 0}
        onClick={() => sampleDevice(props.deviceId || 0)}
      >
        <span className="d-none d-lg-inline-block d-xl-none d-xxl-inline-block">Sample Device</span>
        <span className="d-inline-block d-lg-none d-xl-inline-block d-xxl-none">Sample</span>
      </button>
    </Fragment>
  );
}

SampleDeviceDataButton.propTypes = {
  deviceId: PropTypes.number.isRequired,
  onDeviceRead: PropTypes.func.isRequired,
  onDeviceError: PropTypes.func.isRequired,
};

interface Props {
  deviceId: number;
  onDeviceRead?: () => void;
  onDeviceError?: (errorMessage: string) => void;
}

interface ResponseBody {
  hasUpdated: boolean;
}
